import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { updateMiddleCategory, deleteMiddleCategory, fetchCategories } from '../services/api';

const MiddleCategoryTable = ({ middleCategories, onRefresh, loading }) => {
  const [editingMiddleCategory, setEditingMiddleCategory] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    categoryId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  // Fetch categories when component mounts
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, []);

  const handleEdit = (middleCategory) => {
    setEditingMiddleCategory(middleCategory.id);
    setEditFormData({
      name: middleCategory.name,
      categoryId: middleCategory.categoryId,
    });
  };

  const handleCancelEdit = () => {
    setEditingMiddleCategory(null);
    setEditFormData({
      name: '',
      categoryId: '',
    });
  };

  const handleUpdate = async (id) => {
    if (!editFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      // Format data with categoryId as integer
      const updateData = {
        name: editFormData.name.trim(),
        categoryId: parseInt(editFormData.categoryId)
      };
      
      await updateMiddleCategory(id, updateData);
      toast.success('Middle category updated successfully');
      setEditingMiddleCategory(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update middle category');
      console.error('Error updating middle category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this middle category?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteMiddleCategory(id);
      toast.success('Middle category deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete middle category');
      console.error('Error deleting middle category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (middleCategories.length === 0) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
        No middle categories found.
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subcategory Count
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {middleCategories.map((middleCategory) => (
              <tr key={middleCategory.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {middleCategory.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {editingMiddleCategory === middleCategory.id ? (
                    <input
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Middle category name"
                      disabled={isSubmitting}
                    />
                  ) : (
                    middleCategory.name
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {editingMiddleCategory === middleCategory.id ? (
                    <select
                      name="categoryId"
                      value={editFormData.categoryId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      disabled={isSubmitting}
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    middleCategory.categoryName
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {middleCategory.subcategoryCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {editingMiddleCategory === middleCategory.id ? (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleUpdate(middleCategory.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        disabled={isSubmitting}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-gray-600 hover:text-gray-900"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(middleCategory)}
                        className="text-indigo-600 hover:text-indigo-900"
                        disabled={isSubmitting}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(middleCategory.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={isSubmitting}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {middleCategories.map((middleCategory) => (
          <div key={middleCategory.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              {editingMiddleCategory === middleCategory.id ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor={`name-${middleCategory.id}`} className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      id={`name-${middleCategory.id}`}
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Middle category name"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor={`category-${middleCategory.id}`} className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <select
                      id={`category-${middleCategory.id}`}
                      name="categoryId"
                      value={editFormData.categoryId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      disabled={isSubmitting}
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUpdate(middleCategory.id)}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={isSubmitting}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <h3 className="text-lg font-medium text-gray-900">{middleCategory.name}</h3>
                    <span className="text-sm text-gray-500">ID: {middleCategory.id}</span>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-500">Category:</span>
                    <p className="text-sm text-gray-900">{middleCategory.categoryName}</p>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-500">Subcategory Count:</span>
                    <p className="text-sm text-gray-900">{middleCategory.subcategoryCount}</p>
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => handleEdit(middleCategory)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      disabled={isSubmitting}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(middleCategory.id)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default MiddleCategoryTable;
